import { request } from './core'

export interface AuditEntry {
  id: string
  at: string
  actor: string | null
  company_slug: string | null
  display_name: string | null
  category: string
  action: string
  summary: string
  detail: { changes?: AuditChange[]; [k: string]: unknown } | null
}

export interface AuditChange {
  path: string
  before: unknown
  after: unknown
}

export interface AuditPage {
  items: AuditEntry[]
  total: number
  page: number
  page_size: number
}

export interface AuditAssessment {
  slug: string
  display_name: string
}

export interface PlaybookEvidenceCall {
  service: string
  operations: string[]
  looks_for: string
}

export interface PlaybookPlay {
  id: string
  ref: string | null
  pillar: string
  tier: 'agent' | 'confirm' | 'human'
  gating: boolean
  title: string
  ask: string
  resolve: string
  evidence: PlaybookEvidenceCall[]
  source: string[]
  verified: string
  review_after: string
  /** Fields the admin has overridden (empty = pristine playbook prose). */
  overridden_fields: string[]
  /** The playbook base value for each overridden field. */
  base: Record<string, string>
}

export interface PlaybooksResponse {
  wafr: {
    version: number
    editable_fields: string[]
    plays: PlaybookPlay[]
  }
}

export const adminApi = {
  listAudit: (params: {
    slug?: string | undefined
    actor?: string | undefined
    page?: number
    page_size?: number
  }) => {
    const qs = new URLSearchParams()
    if (params.slug) qs.set('slug', params.slug)
    if (params.actor) qs.set('actor', params.actor)
    if (params.page) qs.set('page', String(params.page))
    if (params.page_size) qs.set('page_size', String(params.page_size))
    const q = qs.toString()
    return request<AuditPage>(`/api/admin/audit${q ? `?${q}` : ''}`)
  },

  listAuditAssessments: () =>
    request<{ assessments: AuditAssessment[] }>('/api/admin/audit/assessments'),

  listAuditActors: () => request<{ actors: string[] }>('/api/admin/audit/actors'),

  getPlaybooks: () => request<PlaybooksResponse>('/api/admin/playbooks'),

  /** Save the prose override for one WAFR question play. Blank / base-equal
   *  values revert that field; reverting everything drops the override. */
  putPlayOverride: (playId: string, patch: { title?: string; ask?: string; resolve?: string }) =>
    request<{ play_id: string; overridden_fields: string[] }>(
      `/api/admin/playbooks/wafr/${playId}`,
      { method: 'PUT', body: JSON.stringify(patch) },
    ),

  deletePlayOverride: (playId: string) =>
    request<{ play_id: string; overridden_fields: string[] }>(
      `/api/admin/playbooks/wafr/${playId}`,
      { method: 'DELETE' },
    ),
}
